import React, { useMemo, useState } from "react";

/**
 * CompanyIdentityHeader — Phase 8.LP.1 + Phase 8.LP.POLISH
 * ---------------------------------------------------------
 * Unified premium hero-header for the /analyze page.
 *
 * Phase 8.LP.POLISH consolidates what used to be TWO separate
 * header blocks (the legacy ticker/price header and the earlier
 * standalone identity header) into a single component with well-
 * defined optional slots:
 *
 *   - ``price``        — currency-formatted current price (rendered
 *                        as tabular monospace next to the name).
 *   - ``badges``       — array of pre-rendered React nodes shown
 *                        below the metadata line (status pill, mode
 *                        chip, live-data indicator, etc.). Ordering
 *                        + gating is the caller's responsibility.
 *   - ``rightSlot``    — React node rendered on the right side of
 *                        the header — typically the Watchlist and
 *                        Re-analyze buttons.
 *
 * The component is otherwise unchanged: logo tile with initials
 * fallback, safe ``onError`` swap (no broken-image icon), truncating
 * name + metadata line. Meta pills render only for present fields
 * so we never show "AAPL · null · null".
 *
 * Design tokens are consumed verbatim from the existing dark-premium
 * palette (font-display, font-mono tabular, border/muted/foreground).
 * No new global CSS is introduced.
 */

function initialsFromTickerOrName(identity) {
  const source = (identity?.ticker || identity?.name || "?").toString().trim();
  if (!source) return "?";
  const alnum = source.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  if (!alnum) return "?";
  return alnum.slice(0, 2);
}

function joinMeta(...parts) {
  return parts.filter((p) => typeof p === "string" && p.trim().length > 0);
}

export function CompanyIdentityHeader({
  identity,
  price = null,
  badges = null,
  rightSlot = null,
  className = "",
}) {
  const [logoBroken, setLogoBroken] = useState(false);
  const initials = useMemo(() => initialsFromTickerOrName(identity), [identity]);

  if (!identity) return null;

  const showLogo =
    typeof identity.logo_url === "string" &&
    identity.logo_url.length > 0 &&
    !logoBroken;

  const displayName = identity.name || identity.ticker || "—";

  const exchange = identity.exchange_short || identity.exchange || null;
  const metaParts = joinMeta(
    identity.ticker,
    exchange,
    identity.sector,
    identity.industry,
    identity.country
  );

  // Filter out null/falsy badge entries so callers can freely
  // conditionally push into the array without having to filter
  // themselves. Nothing is rendered when the badges row is empty.
  const badgeNodes = Array.isArray(badges) ? badges.filter(Boolean) : (badges ? [badges] : []);

  return (
    <header
      className={`flex items-start sm:items-center gap-4 flex-col sm:flex-row sm:justify-between ${className}`}
      data-testid="company-identity-header"
    >
      <div className="flex items-start gap-4 min-w-0 w-full sm:w-auto">
        {/* Logo tile (48x48 mobile / 64x64 desktop) */}
        <div
          className="relative shrink-0 rounded-2xl overflow-hidden border border-border/70 bg-muted/40 flex items-center justify-center h-12 w-12 sm:h-16 sm:w-16"
          data-testid="company-identity-logo-tile"
        >
          {showLogo ? (
            <img
              src={identity.logo_url}
              alt={`${displayName} logo`}
              className="h-full w-full object-contain p-1.5"
              loading="lazy"
              data-testid="company-identity-logo-img"
              onError={() => setLogoBroken(true)}
            />
          ) : (
            <span
              aria-label={`${displayName} placeholder logo`}
              className="font-display text-base sm:text-lg font-semibold tracking-tight text-foreground/80 select-none"
              data-testid="company-identity-logo-fallback"
            >
              {initials}
            </span>
          )}
        </div>

        {/* Name + meta + price + badges */}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1
              className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-[-0.01em] leading-tight truncate max-w-full"
              data-testid="company-identity-name"
            >
              {displayName}
            </h1>
            {price ? (
              <span
                className="font-mono tabular text-xl sm:text-2xl lg:text-3xl text-foreground/95 whitespace-nowrap"
                data-testid="company-identity-price"
              >
                {price}
              </span>
            ) : null}
          </div>
          {metaParts.length > 0 && (
            <p
              className="mt-1 text-xs sm:text-sm text-muted-foreground truncate"
              data-testid="company-identity-meta"
            >
              {metaParts.map((p, i) => (
                <React.Fragment key={`${p}-${i}`}>
                  {i > 0 && (
                    <span aria-hidden className="mx-1.5 opacity-60">
                      ·
                    </span>
                  )}
                  <span
                    className={i === 0 ? "font-mono tracking-[0.02em]" : ""}
                    data-testid={i === 0 ? "company-identity-ticker" : undefined}
                  >
                    {p}
                  </span>
                </React.Fragment>
              ))}
            </p>
          )}
          {badgeNodes.length > 0 && (
            <div
              className="mt-3 flex items-center gap-2 flex-wrap"
              data-testid="company-identity-badges"
            >
              {badgeNodes.map((n, i) => (
                <React.Fragment key={i}>{n}</React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>

      {rightSlot ? (
        <div
          className="flex items-center gap-2 shrink-0 w-full sm:w-auto justify-end"
          data-testid="company-identity-actions"
        >
          {rightSlot}
        </div>
      ) : null}
    </header>
  );
}

export default CompanyIdentityHeader;
